import { updateDisease } from '../hooks/useDisease';
import DatePicker from '@oncolens/oncolens-ui/lib/components/FormControls/DatePicker';
import Listbox from '@oncolens/oncolens-ui/lib/components/FormControls/Listbox';
import TextInput from '@oncolens/oncolens-ui/lib/components/FormControls/TextInput';
import Combobox, {
  ComboboxItem,
} from '@oncolens/oncolens-ui/lib/components/FormControls/Combobox/Combobox';
import { FormStoreProps } from '@oncolens/oncolens-ui/lib/libraries/FormStore';
import clsx from 'clsx';
import React from 'react';
import { FaTrash } from 'react-icons/fa';
import shallow from 'zustand/shallow';
import * as Disease from '../domain/Disease';
import { createDiseaseForEvent } from '../hooks/useEvent';
import useRequestFormStore from '../store';
import DiseaseEpisodeList from './DiseaseEpisodeList';
import FormField from '@oncolens/oncolens-ui/lib/libraries/FormField';
import { usePostCaseConfigs } from '../hooks/PostCaseUtilities';
import DeleteDiseaseModal from './DeleteDiseaseModal';
import { EpisodeDto } from '../dtos/Episode';

interface DiseaseItemProps {
  id: string;
  eventId: string;
}

function DiseaseItem({ id, eventId }: DiseaseItemProps) {
  const formKey = `diseases.${id}`;
  const [
    locationValue,
    diseaseTypeValue,
    isSaved,
    isPrimary,
    updateField,
    getValue,
    getValues,
    updateStore,
  ] = useRequestFormStore(
    (state) => [
      state.diseases?.[id]?.cancerLocationId,
      state.diseases?.[id]?.cancerPathologyId,
      state.diseases?.[id]?.isSaved,
      state.diseases?.[id]?.isPrimary,
      state.updateField,
      state.getValue,
      state.getValues,
      state.updateStore,
    ],
    shallow
  );

  const configs = usePostCaseConfigs([
    'cancer',
    'behavior',
    'setting',
    'recurrence',
    'recurrence_areas',
    'areas_of_metastasis',
  ]);

  const { pathologies } = Disease.getSubCollectionsFromCancerType(configs?.cancer, locationValue);
  const isLateral = Disease.isLocationLateral(locationValue, configs?.cancer);
  const [pathologyOtherIds, locations, cancers] = React.useMemo(() => {
    const locations = Disease.mapCancerConfig<ComboboxItem>(configs?.cancer);
    return [
      Disease.getCancerConfigPathologyOtherUuids(configs?.cancer),
      locations,
      configs?.cancer,
    ];
  }, [configs?.cancer]);

  const handleDelete = () => {
    updateStore((state) => {
      delete state.diseases[id];
      state.markClean(formKey);
    });
  };

  return (
    <div className="relative grid grid-cols-2 gap-3 sm:grid-cols-7">
      {!isPrimary ? (
        isSaved ? (
          <DeleteDiseaseModal diseaseId={id} />
        ) : (
          <FaTrash
            onClick={handleDelete}
            className="box-content absolute top-0 right-0 p-2 ml-auto text-gray-600 transition-colors duration-200 cursor-pointer hover:text-gray-800"
          />
        )
      ) : null}

      <FormField
        name={`${formKey}.cancerLocationId`}
        store={useRequestFormStore}
        validationOptions={{
          valueProvider: getValues,
        }}
      >
        {({ value, onChange, errorMessage }) => {
          let changed = (selectedItem: any) => (item: any) => {
            updateField(
              `${formKey}.cancerTypeId`,
              item
                ? cancers?.find((c) => c.locations.find((l) => l.value === item.value))?.value
                : null
            );
            // clear staging values
            updateStore((store) => {
              Object.keys(store.diseases).forEach((diseaseId) => {
                if (diseaseId === id) {
                  Object.keys(store.diseases[diseaseId].episodes).forEach(
                    (episodeId) => {
                      if (store.diseases[diseaseId].episodes[
                        episodeId
                      ].isTargetEpisode) {
                        store.diseases[diseaseId].episodes[
                          episodeId
                        ].staging = {};
                      }
                    }
                  )
                }
              });
            });
            updateField(`${formKey}.cancerPathologyId`, null);
            onChange(item ? item.value : null);
          };
          return (
            <Combobox
              id="location-typeahead"
              htmlName="location-typeahead"
              label="Location (or Topography)"
              data={locations}
              error={errorMessage}
              value={locations.find((location) => location.value === value) as ComboboxItem}
              placeholder="Start typing a location..."
              required
              useAutocomplete
              wrapperProps={{ className: 'col-span-2 sm:col-span-3' }}
              onChange={changed(locations.find((location) => location.value === value) || null)}
            />
          );
        }}
      </FormField>

      <FormField
        name={`${formKey}.cancerPathologyId`}
        store={useRequestFormStore}
        validationOptions={{
          validate: Boolean(eventId),
          valueProvider: getValues,
        }}
      >
        {({ value, onChange, errorMessage }) => (
          <Listbox
            label="Disease Type"
            required={true}
            data={pathologies}
            error={errorMessage}
            selected={value}
            setSelected={(selected) => {
              updateField(`${formKey}.cancerPathologyOtherComments`, null);
              onChange(selected);
            }}
            wrapperProps={{
              className: clsx('col-span-2'),
            }}
          />
        )}
      </FormField>
      <FormField
        name={`${formKey}.initialDiagnosisDate`}
        store={useRequestFormStore}
        validationOptions={{
          valueProvider: getValues,
        }}
      >
        {({ value, onChange, errorMessage }) => (
          <DatePicker
            label="Date of Initial Diagnosis"
            id={'initialDiagnosisDate'}
            htmlName={'initialDiagnosisDate'}
            required
            error={errorMessage}
            selected={value}
            setSelected={onChange}
            wrapperProps={{ className: 'col-span-2' }}
            inputProps={{ maxDate: new Date() }}
          />
        )}
      </FormField>
      {Boolean(pathologyOtherIds) && pathologyOtherIds.some((x) => x === diseaseTypeValue) && (
        <FormField
          name={`${formKey}.cancerPathologyOtherComments`}
          store={useRequestFormStore}
          validationOptions={{
            valueProvider: getValues,
          }}
        >
          {({ value, onChange }) => (
            <TextInput
              type="text"
              id="cancerPathologyOtherComments"
              htmlName="cancerPathologyOtherComments"
              value={value}
              onChange={onChange}
              label="Comments"
              wrapperProps={{
                className: 'col-span-1 sm:col-span-3 sm:col-start-4 sm:col-end-7',
              }}
            />
          )}
        </FormField>
      )}

      <DiseaseEpisodeList diseaseId={id} isCancerTypeLateral={isLateral} allowAdd={false} />
    </div>
  );
}

function resolvePatientHistory(store: FormStoreProps<any>) {
  const patientHistory = { ...store.patientHistory };
  const options = store.formOptions;

  if (
    options.chiefComplaintTemplate &&
    patientHistory.chiefComplaint === options.chiefComplaintTemplate
  ) {
    patientHistory.chiefComplaint = null;
  }

  if (
    options.familyHistoryTemplate &&
    patientHistory.familyHistory === options.familyHistoryTemplate
  ) {
    patientHistory.familyHistory = null;
  }

  if (
    options.medicalHistoryTemplate &&
    patientHistory.medicalHistory === options.medicalHistoryTemplate
  ) {
    patientHistory.medicalHistory = null;
  }

  if (
    options.miscCaseDetailsTemplate &&
    patientHistory.miscellaneousCaseDetails === options.miscCaseDetailsTemplate
  ) {
    patientHistory.miscellaneousCaseDetails = null;
  }

  return patientHistory;
}

DiseaseItem.create = async (eventId: string, diseaseId: string) => {
  const store = useRequestFormStore.getState();
  const diseaseToCreate = store.diseases[diseaseId];
  const targetEpisodeKey = Object.keys(diseaseToCreate.episodes).find(
    (key) => diseaseToCreate.episodes[key].isTargetEpisode
  );

  if (!targetEpisodeKey) {
    throw 'No target episode was found';
  }

  let data = {
    ...diseaseToCreate,
    meetingId: store.event.meetingId,
    targetEpisode: diseaseToCreate.episodes[targetEpisodeKey],
  };

  if (diseaseToCreate.isPrimary) {
    data = Object.assign(data, resolvePatientHistory(store));
  }

  return createDiseaseForEvent(eventId, data).then((result) => {
    const targetEpisode = result.episodes.find((episode) => episode.isTargetEpisode);

    store.updateStore((store: any) => {
      store.diseases[result.id] = {
        ...result,
        tempId: diseaseId,
        episodes: {
          [(targetEpisode as EpisodeDto).id]: {
            ...targetEpisode,
            isSaved: true,
          },
        },
        isSaved: true,
      };

      delete store.diseases[diseaseId];
    });
  });
};

DiseaseItem.createOrUpdate = async (diseaseId: string, eventId: string) => {
  const store = useRequestFormStore.getState();
  const disease = store.diseases[diseaseId];

  if (disease.isSaved) {
    return updateDisease(diseaseId, {
      ...disease,
      ...(disease.isPrimary ? resolvePatientHistory(store) : {}),
    });
  } else {
    return DiseaseItem.create(eventId, diseaseId);
  }
};

export default DiseaseItem;
